import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { History, User, PenLine, Trash2, PlusCircle, Sparkles } from "lucide-react";

const EVENT_META = {
  created: { icon: PlusCircle, cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  create: { icon: PlusCircle, cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  update: { icon: PenLine, cls: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300" },
  reviewed: { icon: History, cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  delete: { icon: Trash2, cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

export default function CarePlanReviewHistory({ planId }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    api.get(`/care-plan-wave2/${planId}/history`)
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => setEvents([]))
      .finally(() => setLoading(false));
  }, [planId]);

  if (!planId) return null;
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4" data-testid="care-plan-history">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
        <History className="w-3 h-3" /> Review History
      </div>
      {loading ? (
        <div className="text-xs text-slate-500">Loading history…</div>
      ) : !events || events.length === 0 ? (
        <div className="text-xs text-slate-400">No review events yet.</div>
      ) : (
        <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-3 space-y-4">
          {events.map((e, i) => {
            const meta = EVENT_META[e.kind] || EVENT_META.update;
            const Icon = meta.icon;
            return (
              <li key={i} className="ml-4" data-testid={`history-event-${i}`}>
                <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${meta.cls}`}>
                  <Icon className="w-3 h-3" />
                </span>
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{fmt(e.at)}</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  {e.label}{e.actor ? ` by ${e.actor}` : ""}
                </div>
                {e.summary && <div className="text-[11px] text-slate-500 mt-0.5">{e.summary}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
